import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import apiClient from '../services/apiClient';
import { allowAISharing, revokeAISharing, AI_SHARING_REQUEST, AI_SHARING_VERSION, type AISharingRequest, type AISharingPreferences } from '../services/aiSharingService';
import { PUBLIC_RESOURCE_URLS } from '../config/publicResources';

export default function AISharingDialog() {
  const { authSession, authUser, policyStatus } = useApp();
  const [request, setRequest] = useState<AISharingRequest | null>(null);
  const pending = useRef<AISharingRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState<AISharingPreferences>({ personalization: false, private_notes: false, background_learning: false });
  const [extrasOpen, setExtrasOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const receive = (event: Event) => {
      const incoming = (event as CustomEvent<AISharingRequest>).detail;
      if (!authSession || incoming.token !== apiClient.getToken() || incoming.signal?.aborted) return;
      event.preventDefault();
      pending.current.push(incoming);
      setRequest(current => current ?? incoming);
    };
    window.addEventListener(AI_SHARING_REQUEST, receive);
    return () => {
      window.removeEventListener(AI_SHARING_REQUEST, receive);
      pending.current.splice(0).forEach(value => value.resolve(false));
      setRequest(null);
      setBusy(false);
    };
  }, [authSession]);

  useEffect(() => {
    if (!request) return;
    setError('');
    setExtrasOpen(request.manage || request.status.allowed);
    setPreferences({ personalization: request.status.allowed && request.status.personalization === true,
      private_notes: request.status.allowed && request.status.private_notes === true,
      background_learning: request.status.allowed && request.status.background_learning === true });
    const previous = document.activeElement as HTMLElement | null;
    titleRef.current?.focus({ preventScroll: true });
    return () => { if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, [request]);
  useEffect(() => {
    if (!request?.signal) return;
    const discardAborted = () => {
      pending.current = pending.current.filter(value => !value.signal?.aborted);
      setRequest(pending.current[0] ?? null);
    };
    if (request.signal.aborted) discardAborted();
    else request.signal.addEventListener('abort', discardAborted, { once: true });
    return () => request.signal?.removeEventListener('abort', discardAborted);
  }, [request]);
  const finish = (allowed: boolean) => {
    pending.current.splice(0).forEach(value => value.resolve(allowed));
    setRequest(null);
  };
  if (!request) return null;
  const providers = request.status.providers ?? [];
  const current = request.status.disclosure_version === AI_SHARING_VERSION && providers.length > 0;
  const canAllow = authUser?.is_verified === true && policyStatus?.accepted === true;
  const change = async (allow: boolean) => {
    if (apiClient.getToken() !== request.token || request.signal?.aborted) { finish(false); return; }
    setBusy(true);
    setError('');
    try {
      if (allow) await allowAISharing(request.status.disclosure_version, providers.map(provider => provider.id), preferences, authSession?.signal);
      else await revokeAISharing(authSession?.signal);
      if (apiClient.getToken() !== request.token) return;
      finish(allow);
    } catch {
      if (apiClient.getToken() === request.token) setError('Could not save your choice. Please try again.');
    } finally { setBusy(false); }
  };
  return <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-5" onKeyDown={event => {
    if (event.key === 'Escape' && !busy) finish(false);
    if (event.key === 'Tab') {
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href]')];
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === titleRef.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }}>
    <section role="dialog" aria-modal="true" aria-labelledby="ai-sharing-title" aria-describedby="ai-sharing-description" className="w-full max-w-sm max-h-[85dvh] overflow-y-auto rounded-2xl border p-5" style={{ backgroundColor: 'var(--app-surface, #0F1829)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}>
      <h2 ref={titleRef} tabIndex={-1} id="ai-sharing-title" className="text-xl font-semibold">{request.manage || request.status.allowed ? 'Rubikoo AI settings' : 'Enable Rubikoo AI?'}</h2>
      <div id="ai-sharing-description" className="mt-4 space-y-3 text-sm leading-6">
        <p>Basic AI sends your prompts, recent conversation in the current chat, and relevant event content to the AI provider{providers.length === 1 ? '' : 's'} listed below to answer your questions. It does not automatically include your profile, bookmarks or private notes.</p>
        <p>Current AI provider{providers.length === 1 ? '' : 's'}: {providers.map((provider, index) => <span key={provider.id}>{index > 0 ? ', ' : ''}<a href={provider.privacy_url} target="_blank" rel="noopener noreferrer" className="underline">{provider.name}</a></span>)}. A provider or disclosure change requires a fresh choice before sharing.</p>
        <p>AI is optional. You can continue without AI and still use events, schedules and papers. Change your choice anytime in Profile → Rubikoo AI settings.</p>
      </div>
      <button type="button" disabled={busy} aria-expanded={extrasOpen} aria-controls="ai-optional-extras" onClick={() => setExtrasOpen(previous => !previous)} className="mt-4 min-h-12 w-full rounded-xl border px-3 text-left text-sm">
        Optional extras · {Object.values(preferences).filter(Boolean).length} selected {extrasOpen ? '−' : '+'}
      </button>
      <fieldset id="ai-optional-extras" hidden={!extrasOpen} disabled={busy || !current || !canAllow} className="mt-3 space-y-3 text-sm">
        <legend className="mb-2 font-semibold">Optional extras — off unless you choose them</legend>
        {([
          ['personalization', 'Personalize using my profile and bookmarks', 'Share relevant name, title, company, interests and saved event items for recommendations and reports.'],
          ['private_notes', 'Include my private notes', 'Share relevant existing and future notes for AI answers, reports and recaps. Local networking notes are always excluded.'],
          ['background_learning', 'Remember and learn in the background', 'Send chat content for background summaries and learned interests across chats. Profile/bookmarks and private notes are used only if their choices above are also enabled.'],
        ] as const).map(([key, label, description]) => <label key={key} className="flex min-h-12 items-start gap-3 rounded-xl border p-3">
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={preferences[key]} onChange={event => setPreferences(previous => ({ ...previous, [key]: event.target.checked }))} />
          <span><span className="block font-medium">{label}</span><span className="mt-1 block text-xs leading-5">{description}</span></span>
        </label>)}
      </fieldset>
      <div className="mt-4 space-y-2 text-xs leading-5">
        <p>Enabling or re-enabling AI, adding extras, or changing providers starts fresh AI context. Turning extras off keeps recent typed questions and compatible answers, but excludes generated context that may contain the removed data. Saved records are not deleted. Turning AI off stops new processing but cannot recall information already sent. Information you type into a prompt is shared even if a related extra is off.</p>
        <p>Do not submit sensitive information or another person’s private information. AI answers can be inaccurate.</p>
        <p><a href={PUBLIC_RESOURCE_URLS.privacy} target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> · <a href={PUBLIC_RESOURCE_URLS.terms} target="_blank" rel="noopener noreferrer" className="underline">Terms of Use</a></p>
      </div>
      {!current && <p role="alert" className="mt-3 text-sm">Please update the app to review the latest AI disclosure.</p>}
      {!request.status.allowed && !canAllow && <p className="mt-3 text-sm">Verify your email and complete your age and Terms confirmations before enabling AI. You can turn off an existing permission without accepting new Terms.</p>}
      {error && <p role="alert" className="mt-3 text-sm">{error}</p>}
      <div className="mt-5 flex flex-col gap-3">
        <button disabled={busy || !current || !canAllow} onClick={() => void change(true)} className="min-h-12 rounded-xl px-4 text-white" style={{ backgroundColor: 'var(--app-accent-solid)' }}>{busy ? 'Saving…' : request.status.allowed ? 'Save AI choices' : 'Enable AI'}</button>
        {request.status.allowed && <button disabled={busy} onClick={() => void change(false)} className="min-h-12 rounded-xl border px-4">Turn off AI</button>}
        <button ref={closeRef} disabled={busy} onClick={() => finish(false)} className="min-h-12 rounded-xl border px-4">{request.status.allowed ? 'Close' : 'Continue without AI'}</button>
      </div>
    </section>
  </div>;
}
